// Bare shared-library import, not a deep workspacePrefix.js subpath — see
// app.js's identical fix note (tickets/catalog-fixall.md P0).
import { shellStripWorkspacePrefix } from '../functions/shellSharedDelegates.js'

export const SharedViewSwitcher = {
  flow: 'x',
  borderWidth: 'hairline',
  borderColor: 'line',
  borderStyle: 'solid',
  round: 'radiusPill',
  overflow: 'hidden',
  stateKey: 'viewMode',
  mode: 'state'
}

const _stateKey = (el) => (el.parent && el.parent.stateKey) || 'viewMode'
const _mode = (el) => (el.parent && el.parent.mode) || 'state'
// Named distinctly, not `_isActive` — two bundled shared libraries
// (marketplace/components/MarketplaceSurfaceTabs.js,
// shared/components/AppNavbar.js) each declare their own module-scope
// `_isActive`; esbuild's bundler disambiguates the collision by renaming
// this file's copy to `_isActive2`, which frank's classifier then can't
// map back to source (`unresolvable-identifier`). A distinct name sidesteps
// the collision instead of depending on a specific bundler's renaming
// scheme. See tickets/fable.md "decompose workspace-shell's 507 defects".
const _sharedViewIsActive = (el, s) => {
  const root = s && s.root
  if (!root) return false
  if (_mode(el) === 'route') return shellStripWorkspacePrefix(root.route) === el.value
  return root[_stateKey(el)] === el.value
}

// Active state is driven by reactive CSS prop factories rather than
// `isActive` + `.isActive`. smbls v3.14 only wires reactive effects for
// function props that are in the CSS_PROPS_REGISTRY — `isActive` is a
// custom boolean flag, so `applyConditionals` runs the check ONCE at
// creation and freezes it. `background`, `color`, `fontWeight` ARE in
// the registry, so their factories re-run on state change.
export const SharedViewSwitcherBtn = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  align: 'center center',
  gap: 'Z',
  minHeight: 'controlHCompact',
  padding: '0 Z',
  border: 'none',
  cursor: 'pointer',
  fontSize: 'Z',
  theme: 'dialog',
  background: (el, s) => (_sharedViewIsActive(el, s) ? undefined : 'transparent'),
  color: (el, s) => (_sharedViewIsActive(el, s) ? 'title' : 'caption'),
  fontWeight: (el, s) => (_sharedViewIsActive(el, s) ? '700' : '400'),
  ':hover': { color: 'title', background: 'hover' },

  Icon: {
    display: (el) => (el.parent && el.parent.icon ? 'inline-flex' : 'none'),
    icon: (el) => (el.parent && el.parent.icon) || 'state',
    boxSize: 'Z2'
  },
  // Longer multi-word labels (calendar's "Team Now") wrap onto two lines and
  // grow taller than their siblings when the switcher is squeezed — no
  // `flexShrink` guard on the button either, so the label is the first thing
  // to give. Every sibling control in the SAME headers (CalHeader's
  // TodayBtn/CalRailBtn/NewBookingBtn Labels) already declares
  // `whiteSpace: 'nowrap'`; this was the one control in the family without
  // it (fable CAT-5 #3, live-confirmed on the 3-consumer shared switcher:
  // calendar/chat/tickets all extend this same component).
  Label: {
    tag: 'span',
    whiteSpace: 'nowrap',
    text: (el) => (el.parent && el.parent.label) || ''
  },

  onClick: (e, el, s) => {
    e.preventDefault()
    e.stopPropagation()
    if (!s || !s.root) return
    if (_mode(el) === 'route') {
      el.router(el.value, el.getRoot(), {}, { pushState: true, scrollToTop: true })
      return
    }
    s.root.update({ [_stateKey(el)]: el.value })
  }
}
